import { Trans } from '@lingui/macro'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  List,
  ListItem,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import {
  dateFormat,
  datetimeFormat,
  portalLanguagesData
} from 'app/appSettings'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import {
  deleteApplicationConfig,
  getFormPages
} from 'app/services/sfAuth/sfData/sfForms'
import {
  getCaseTypesForFundingStreams,
  saveFundingStreams
} from 'app/services/sfAuth/sfData/sfFundingStream'
import { getRecordTypes } from 'app/services/sfAuth/sfData/sfOpportunity'
import { CustomAutocomplete } from 'app/views/common-components/CustomAutocomplete'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { CheckboxGroupWithFormik } from 'app/views/common-components/with-formik/CheckboxGroupWithFormik'
import { CheckboxWithFormik } from 'app/views/common-components/with-formik/CheckboxWithFormik'
import { FormikFastTextField } from 'app/views/common-components/with-formik/FormikFastTextField'
import { PortalTabHeader } from 'app/views/common/PortalTabHeader'
import Loading from 'egret/components/EgretLoadable/Loading'
import { FastField, Field, Formik } from 'formik'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getEmptyLangValues } from 'utils'
import { FUNDING_STREAMS } from '../../../config/configurationMetadata'
import { checkPromiseAllSuccess } from '../../../services/sfAuth/sfData/jsConnectionUtils'
import ProgressSnackbar from '../../common-components/CustomSnackbars'
import FormikFastTextFieldWithDebounce from '../../common-components/with-formik/FormikFastTextFieldWithDebounce'
import MultilanguageTextFieldWithFormik from '../MultilanguageTextFieldWithFormik'

const useStyles = makeStyles((theme) => ({
  FormGroup_grid: {
    flexDirection: 'row',
    marginLeft: 25,
    justifyContent: 'space-between'
  },
  FormControlLabel_grid: {
    width: '45%'
  },
  HelperText_root: {
    marginLeft: 10,
    marginBottom: 8,
    marginTop: 18,
    fontSize: 14,
    lineHeight: 1
  },
  MuiCheckbox_root: {
    padding: 3
  }
}))

export const applicationFundingStreamConfigurationVariables = {
  FUNDING_STREAMS: {
    key: 'FUNDING_STREAMS',
    type: FUNDING_STREAMS,
    sObject: 'Funding_Streams__c'
  }
}

export const matchOpportunityWithFundingStream = ({
  fundingStreams = [],
  fundingStreamId,
  recordTypeId
}) => {
  let toRet

  if (fundingStreamId) {
    toRet = fundingStreams.find(
      (fundingStream) => fundingStream.id === fundingStreamId
    )
    if (toRet) {
      return toRet
    }
  }
  if (!toRet) {
    fundingStreams.some((stream) => {
      if (stream.recordType === recordTypeId) {
        toRet = stream
        return toRet
      }
      return false
    })
  }
  return toRet
}

export const convertTranslationsIfNeeded = (stream) => {
  if (stream && !stream.hasOwnProperty('subtitle')) {
    const updatedStream = { ...stream }
    const translationTexts = [
      'fullName',
      'name',
      'description',
      'moreInfoUrl',
      'subtitle'
    ]
    const languages = ['en', 'fr']
    translationTexts.forEach((text) => {
      updatedStream[text] = getEmptyLangValues()
      languages.forEach((lang) => {
        const property = text + lang.toUpperCase()
        if (updatedStream.hasOwnProperty(property)) {
          const translation = updatedStream[property]
          if (translation) {
            updatedStream[text] = {
              ...updatedStream[text],
              [lang]: translation
            }
          }
        }
      })
    })
    return updatedStream
  }

  return stream
}

export const convertTranslationsForConfigurationsIfNeeded = (config) => {
  if (config && !config.hasOwnProperty('subtitle')) {
    const updatedConfig = { ...config }
    const translationTexts = ['name', 'description', 'moreInfoUrl', 'subtitle']
    const languages = ['en', 'fr']
    translationTexts.forEach((text) => {
      updatedConfig[text] = getEmptyLangValues()
      languages.forEach((lang) => {
        const property = text + lang.toUpperCase()
        if (updatedConfig.hasOwnProperty(property)) {
          const translation = updatedConfig[property]
          if (translation) {
            updatedConfig[text] = {
              ...updatedConfig[text],
              [lang]: translation
            }
          }
        }
      })
    })
    return updatedConfig
  }
  return config
}

const ConfigureFundingStreams = () => {
  const lang = useSelector((state) => state.user.language)
  const [formsData, setFormsData] = React.useState([])
  const [initialValues, setInitialValues] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [saving, setSaving] = React.useState(false)
  const [objectiveRecordTypes, setObjectiveRecordTypes] = React.useState([])
  const [opportunityRecordTypes, setOpportunityRecordTypes] = React.useState([])
  const [caseTypes, setCaseTypes] = React.useState([])
  const [prequalificationRecordTypes, setPrequalificationRecordTypes] =
    React.useState()
  const { fundingStreams, fundingPrograms, refetchFundingStreams } =
    useGlobalQueries()

  useEffect(() => {
    if (fundingStreams.length > 0 && fundingPrograms.length > 0) {
      fetchData()
    }
  }, [fundingStreams])

  const fetchData = useCallback(() => {
    return Promise.all([
      getFormPages(),
      getRecordTypes('Objective__c'),
      getCaseTypesForFundingStreams(),
      getRecordTypes('Pre_Qualification__c'),
      getRecordTypes('Opportunity'),
      refetchFundingStreams()
    ])
      .then(
        ([
          forms,
          objectiveRecordTypes,
          fetchedCaseTypes,
          prequalificaitonRecordTypes,
          opportunityRecordTypes
        ]) => {
          setPrequalificationRecordTypes(
            Object.values(prequalificaitonRecordTypes)
          )
          setOpportunityRecordTypes(Object.values(opportunityRecordTypes))
          const updatedFundingStreams = fundingStreams
          setObjectiveRecordTypes(
            Object.keys(objectiveRecordTypes).map((key) => {
              const item = objectiveRecordTypes[key]
              return {
                id: item.recordTypeId,
                name: item.name
              }
            })
          )
          const formsCount = {}
          forms.forEach((form) => {
            if (form.origin) {
              if (formsCount[form.origin]) {
                formsCount[form.origin].push(form)
              } else {
                formsCount[form.origin] = [form]
              }
            }
          })
          setFormsData(
            forms
              .map((obj) => {
                let name = obj.name[lang] || ''
                if (obj.config.version && formsCount[obj.origin]) {
                  name += ' [v.' + obj.config.version + ']'
                }
                if (obj.comments) {
                  name += ' (' + obj.comments + ')'
                }
                return {
                  name,
                  id: obj.id
                }
              })
              .filter((name) => name)
              .sort((a, b) => String(a.name).localeCompare(b.name))
          )
          setCaseTypes(fetchedCaseTypes)
          const programMap = Object.fromEntries(
            fundingPrograms.map((fp) => [fp.id, fp.name])
          )
          setInitialValues({
            streamList: updatedFundingStreams
              .map((item, index) => {
                let data = {}
                data = item
                data = convertTranslationsIfNeeded(data)
                if (data.startTime && !data.startTime?._isAMomentObject) {
                  const localValue = moment(item.startTime).utc(true)
                  data.startTime = localValue
                }
                if (data.endTime && !data.endTime?._isAMomentObject) {
                  const localValue = moment(item.endTime).utc(true)
                  data.endTime = localValue
                }
                delete data.expanded
                if (!data.disableInProvinces) {
                  data.disableInProvinces = []
                }
                if (!data.avaliableInLanguages) {
                  data.avaliableInLanguages = []
                }

                return {
                  id: item.id,
                  ...data
                }
              })
              .sort((a, b) => {
                const nameA = programMap[a.fundingProgram] || ''
                const nameB = programMap[b.fundingProgram] || ''

                return nameA.localeCompare(nameB, undefined, {
                  sensitivity: 'base'
                })
              })
          })
          setLoading(false)
        }
      )
      .catch((error) => {
        console.error('Error in fetchData:', error)
        setLoading(false)
      })
  }, [fundingStreams, lang])

  const handleSave = (values) => {
    const savingSnackbar = enqueueSnackbar('', {
      persist: true,
      content: (key) => ProgressSnackbar(<Trans>Saving</Trans>)
    })
    setSaving(true)
    return saveFundingStreams(values)
      .then((results) => {
        if (!checkPromiseAllSuccess(results)) {
          return fetchData().then(() => {
            setSaving(false)
            closeSnackbar(savingSnackbar)
            enqueueSnackbar(<Trans>Successfully saved!</Trans>, {
              variant: 'success'
            })
          })
        } else {
          enqueueSnackbar(<Trans>Error occurred while saving!</Trans>, {
            variant: 'error'
          })
          setSaving(false)
          closeSnackbar(savingSnackbar)
          return Promise.reject('checkPromiseAllSuccess returned false')
        }
      })
      .catch((error) => {
        console.error('Error during saveFundingStreams process:', error)
        setSaving(false)
        closeSnackbar(savingSnackbar)
        enqueueSnackbar(<Trans>Error occurred while saving!</Trans>, {
          variant: 'error'
        })
      })
  }

  if (loading || !initialValues) {
    return <Loading atCenter />
  }

  const { formatToTimeZone } = require('date-fns-timezone')

  return (
    <Formik enableReinitialize initialValues={initialValues}>
      {({ values, setFieldValue }) => {
        const { streamList } = values
        return (
          <div className='portal-tab-container'>
            <PortalTabHeader
              header={<Trans>FUNDING_STREAMS_EDITOR_HEADER</Trans>}
            />
            <Box
              sx={{
                top: 0,
                position: 'sticky',
                display: 'flex',
                justifyContent: 'flex-end',
                zIndex: 1000,
                width: '100%',
                paddingTop: '32px',
                paddingBottom: '32px',
                backgroundColor: '#f9f9f6'
              }}
            >
              <Button
                disabled={saving}
                variant='contained'
                color='primary'
                sx={{
                  display: 'flex',
                  padding: '8px 16px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onClick={() => {
                  handleSave(values)
                }}
              >
                <Trans>Save</Trans>
                <Icon style={{ marginLeft: 5 }}>save</Icon>
              </Button>
            </Box>

            <List sx={{ p: 0 }}>
              {streamList.map((item, index) => {
                const { expanded } = item

                return (
                  <ListItem key={index} sx={{ px: 0, py: 2 }}>
                    <Card
                      style={{
                        flexGrow: 1,
                        padding: 20,
                        border: '1px solid #C4C4C4'
                      }}
                    >
                      <Grid
                        justifyContent='space-between'
                        container
                        wrap='nowrap'
                      >
                        <IconButton
                          onClick={() => {
                            setFieldValue(
                              `streamList.${index}.expanded`,
                              !expanded
                            )
                          }}
                          size='large'
                        >
                          <Icon>
                            {expanded ? 'expand_less' : 'expand_more'}
                          </Icon>
                        </IconButton>

                        <Grid item>
                          <IconButton
                            disabled={index === 0}
                            onClick={() => {
                              const toSet = [...streamList]
                              const toMove = toSet[index]
                              const toReplace = toSet[index - 1]
                              toSet[index - 1] = toMove
                              toSet[index] = toReplace
                              setFieldValue('streamList', toSet)
                            }}
                            size='large'
                          >
                            <Icon>arrow_upward</Icon>
                          </IconButton>
                          <IconButton
                            disabled={index + 1 === streamList.length}
                            onClick={() => {
                              const toSet = [...streamList]
                              const toMove = toSet[index]
                              const toReplace = toSet[index + 1]
                              toSet[index + 1] = toMove
                              toSet[index] = toReplace
                              setFieldValue('streamList', toSet)
                            }}
                            size='large'
                          >
                            <Icon>arrow_downward</Icon>
                          </IconButton>
                          <DeleteFundingStreamButton
                            handleDelete={(e) => {
                              const toDelete = streamList[index]
                              const toSet = [...streamList]
                              if (!toDelete.id) {
                                toSet.splice(index, 1)
                                setFieldValue('streamList', toSet)
                                enqueueSnackbar(
                                  <Trans>Funding stream deleted!</Trans>,
                                  {
                                    variant: 'success'
                                  }
                                )
                                return Promise.resolve()
                              } else {
                                return deleteApplicationConfig(
                                  toDelete.id
                                ).then(
                                  (result) => {
                                    return fetchData().then(() => {
                                      enqueueSnackbar(
                                        <Trans>Funding stream deleted!</Trans>,
                                        {
                                          variant: 'success'
                                        }
                                      )
                                      toSet.splice(index, 1)
                                      setFieldValue('streamList', toSet)
                                    })
                                  },
                                  (reject) => {
                                    enqueueSnackbar(
                                      <Trans>
                                        Error occurred while deleting!
                                      </Trans>,
                                      {
                                        variant: 'error'
                                      }
                                    )
                                  }
                                )
                              }
                            }}
                          />
                        </Grid>
                      </Grid>

                      {/*
                      <FormikFastTextField
                        name={`streamList.${index}.shortName`}
                        label={<Trans>Name</Trans>}
                        style={{ padding: 5 }}
                      /> */}

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                            paddingRight: 2,
                            paddingLeft: 2,
                            marginBottom: 2
                          }}
                        >
                          <FormTopLabel size='small'>
                            <Trans>Full name</Trans>
                          </FormTopLabel>
                          <MultilanguageTextFieldWithFormik
                            name={`streamList.${index}.fullName`}
                            lang={lang}
                            inputDebounce
                          />

                          <FormTopLabel size='small'>
                            <Trans>Short name</Trans>
                          </FormTopLabel>
                          <MultilanguageTextFieldWithFormik
                            name={`streamList.${index}.name`}
                            lang={lang}
                            inputDebounce
                            onlySelectTranslation
                          />

                          <FormTopLabel size='small'>
                            <Trans>Short description</Trans>
                          </FormTopLabel>
                          <MultilanguageTextFieldWithFormik
                            name={`streamList.${index}.description`}
                            lang={lang}
                            inputDebounce
                            onlySelectTranslation
                          />

                          <FormTopLabel size='small'>
                            <Trans>Funding program</Trans>
                          </FormTopLabel>
                          <MultilanguageFormSelectField
                            name={`streamList.${index}.fundingProgram`}
                            formsData={fundingPrograms}
                            lang={lang}
                            fullWidth
                          />

                          <FormTopLabel size='small'>
                            <Trans>Notes</Trans>
                          </FormTopLabel>
                          <FormikFastTextFieldWithDebounce
                            name={`streamList.${index}.notes`}
                            fullWidth
                            inputDebounce
                          />
                        </Box>

                        <Collapse in={expanded} sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                              width: '100%',
                              padding: 2
                            }}
                          >
                            <FormTopLabel size='small'>
                              <Trans>Application form</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              formsData={formsData}
                              name={`streamList.${index}.form`}
                            />

                            <FormTopLabel size='small'>
                              <Trans>Eligibility quiz</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.quiz`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>
                                FS_CONFIG_APPLICATION_VIEW_FOR_HARDCODED
                              </Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.applicationViewForHardcoded`}
                              formsData={formsData}
                            />

                            <Typography
                              style={{
                                padding: '10px 10px 10px 0',
                                fontWeight: 'bold'
                              }}
                            >
                              <Trans>PREQUALIFICATION</Trans>
                            </Typography>

                            <FormTopLabel size='small'>
                              <Trans>PREQUALIFICATION_FORM</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.prequalificationForm`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>PREQUALIFICATION_ELIGIBILITY_QUIZ</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.prequalificationEligibilityQuiz`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>PREQUALIFICATION_RECORD_TYPE</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.prequalificationRecordType`}
                              formsData={prequalificationRecordTypes.map(
                                (obj) => ({
                                  id: obj.recordTypeId,
                                  name: obj.name
                                })
                              )}
                            />

                            <FormTopLabel size='small'>
                              <Trans>DMAH_FORM</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.dmahView`}
                              formsData={formsData}
                            />

                            <Typography
                              style={{
                                padding: '10px 10px 10px 0',
                                fontWeight: 'bold'
                              }}
                            >
                              <Trans>Grantee reports</Trans>
                            </Typography>

                            <FormTopLabel size='small'>
                              <Trans>Grantee report form</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.granteeReportForm`}
                              formsData={formsData}
                            />

                            <Typography
                              style={{
                                padding: '10px 10px 10px 0',
                                fontWeight: 'bold'
                              }}
                            >
                              <Trans>Program Manager</Trans>
                            </Typography>

                            <FormTopLabel size='small'>
                              <Trans>Funding agreement</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.contract`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>Selection Committee preview</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.SC`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>Allocation Committee preview</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.AC`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>Board summary preview</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.Board`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>CMHC summary preview</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.CMHC`}
                              formsData={formsData}
                            />

                            <FormTopLabel size='small'>
                              <Trans>Case type</Trans>
                            </FormTopLabel>
                            <FormSelectField
                              name={`streamList.${index}.caseType`}
                              formsData={caseTypes}
                            />

                            <Typography
                              style={{
                                padding: '10px 10px 10px 0',
                                fontWeight: 'bold'
                              }}
                            >
                              <Trans>Record Types</Trans>
                            </Typography>

                            <FormTopLabel size='small'>
                              <Trans>Objective record type</Trans>
                            </FormTopLabel>
                            <FormikFastTextField
                              name={`streamList.${index}.objectiveRecordType`}
                              style={{ marginBottom: 10 }}
                              select
                            >
                              {objectiveRecordTypes.map((item, index) => (
                                <MenuItem key={index} value={item.id}>
                                  {item.name}
                                </MenuItem>
                              ))}
                            </FormikFastTextField>

                            <FormTopLabel size='small'>
                              <Trans>Application record type</Trans>
                            </FormTopLabel>
                            <FormikFastTextField
                              name={`streamList.${index}.applicationRecordType`}
                              style={{ marginBottom: 10 }}
                              select
                            >
                              {opportunityRecordTypes.map((item, index) => (
                                <MenuItem key={index} value={item.recordTypeId}>
                                  {item.name}
                                </MenuItem>
                              ))}
                            </FormikFastTextField>

                            <CheckboxGroupWithFormik
                              label={<Trans>Disable in provinces</Trans>}
                              style={{ padding: 5, width: '100%' }}
                              name={`streamList.${index}.disableInProvinces`}
                              compact
                              renderInTwoColumns
                              options={[
                                {
                                  label: <Trans>Alberta</Trans>,
                                  value: 'Alberta'
                                },
                                {
                                  label: <Trans>British Columbia</Trans>,
                                  value: 'British Columbia'
                                },
                                {
                                  label: <Trans>Manitoba</Trans>,
                                  value: 'Manitoba'
                                },
                                {
                                  label: <Trans>New Brunswick</Trans>,
                                  value: 'New Brunswick'
                                },
                                {
                                  label: (
                                    <Trans>Newfoundland and Labrador</Trans>
                                  ),
                                  value: 'Newfoundland and Labrador'
                                },
                                {
                                  label: <Trans>Northwest Territories</Trans>,
                                  value: 'Northwest Territories'
                                },
                                {
                                  label: <Trans>Nova Scotia</Trans>,
                                  value: 'Nova Scotia'
                                },
                                {
                                  label: <Trans>Nunavut</Trans>,
                                  value: 'Nunavut'
                                },
                                {
                                  label: <Trans>Ontario</Trans>,
                                  value: 'Ontario'
                                },
                                {
                                  label: <Trans>Prince Edward Island</Trans>,
                                  value: 'Prince Edward Island'
                                },
                                {
                                  label: <Trans>Québec</Trans>,
                                  value: 'Quebec'
                                },
                                {
                                  label: <Trans>Saskatchewan</Trans>,
                                  value: 'Saskatchewan'
                                },
                                {
                                  label: <Trans>Yukon</Trans>,
                                  value: 'Yukon'
                                }
                              ]}
                            />

                            <CheckboxGroupWithFormik
                              label={<Trans>Available in languages</Trans>}
                              style={{ padding: 5, width: '100%' }}
                              name={`streamList.${index}.avaliableInLanguages`}
                              multiple
                              row
                              options={Object.values(portalLanguagesData).map(
                                (obj) => {
                                  return {
                                    label: obj.labelTrans,
                                    value: obj.editKey
                                  }
                                }
                              )}
                            />

                            <div>
                              <b>
                                <Trans>Your time zone:</Trans>
                              </b>{' '}
                              {Intl.DateTimeFormat().resolvedOptions().timeZone}
                              {' ('}
                              {new Date().getTimezoneOffset()})
                            </div>

                            <Grid container>
                              <Grid xs={6} item style={{ padding: 5 }}>
                                <Field name={`streamList.${index}.startTime`}>
                                  {({ field, form }) => {
                                    return (
                                      <>
                                        <Grid
                                          container
                                          direction='row'
                                          wrap='nowrap'
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              width: '100%',
                                              gap: 2
                                            }}
                                          >
                                            <FormTopLabel size='small'>
                                              <Trans>Validity start</Trans>
                                            </FormTopLabel>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                width: '100%'
                                              }}
                                            >
                                              <DateTimePicker
                                                {...field}
                                                format={datetimeFormat}
                                                value={field.value || null}
                                                onChange={(e) => {
                                                  form.setFieldValue(
                                                    `streamList.${index}.startTime`,
                                                    e
                                                  )
                                                }}
                                                fullWidth
                                                inputVariant='outlined'
                                                renderInput={(props) => {
                                                  return (
                                                    <TextField
                                                      {...props}
                                                      inputProps={{
                                                        ...props.inputProps,
                                                        value: field.value
                                                          ? props.inputProps
                                                            .value
                                                          : '',
                                                        placeholder: ''
                                                      }}
                                                      fullWidth
                                                      variant='outlined'
                                                    />
                                                  )
                                                }}
                                              />
                                              <div style={{ padding: 5 }}>
                                                <IconButton
                                                  onClick={(e) => {
                                                    form.setFieldValue(
                                                      `streamList.${index}.startTime`,
                                                      null
                                                    )
                                                  }}
                                                  size='large'
                                                >
                                                  <Icon>close</Icon>
                                                </IconButton>
                                              </div>
                                            </Box>
                                          </Box>
                                        </Grid>
                                        {field.value && (
                                          <div style={{ padding: 5 }}>
                                            <b>
                                              <Trans>In Pacific time:</Trans>
                                            </b>{' '}
                                            {formatToTimeZone(
                                              moment
                                                .utc(field.value)
                                                .local(true),
                                              datetimeFormat,
                                              {
                                                timeZone: 'Canada/Pacific'
                                              }
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )
                                  }}
                                </Field>
                              </Grid>
                              <Grid xs={6} item style={{ padding: 5 }}>
                                <Field name={`streamList.${index}.endTime`}>
                                  {({ field, form }) => {
                                    return (
                                      <>
                                        <Grid
                                          container
                                          direction='row'
                                          wrap='nowrap'
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              width: '100%',
                                              gap: 2
                                            }}
                                          >
                                            <FormTopLabel size='small'>
                                              <Trans>Validity end</Trans>
                                            </FormTopLabel>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                width: '100%'
                                              }}
                                            >
                                              <DateTimePicker
                                                {...field}
                                                format={dateFormat + ' h:mm a'}
                                                value={field.value || null}
                                                onChange={(e) => {
                                                  form.setFieldValue(
                                                    `streamList.${index}.endTime`,
                                                    e
                                                  )
                                                }}
                                                fullWidth
                                                inputVariant='outlined'
                                                renderInput={(props) => {
                                                  return (
                                                    <TextField
                                                      {...props}
                                                      inputProps={{
                                                        ...props.inputProps,
                                                        value: field.value
                                                          ? props.inputProps
                                                            .value
                                                          : '',
                                                        placeholder: ''
                                                      }}
                                                      fullWidth
                                                      variant='outlined'
                                                    />
                                                  )
                                                }}
                                              />
                                              <div style={{ padding: 5 }}>
                                                <IconButton
                                                  onClick={(e) => {
                                                    form.setFieldValue(
                                                      `streamList.${index}.endTime`,
                                                      null
                                                    )
                                                  }}
                                                  size='large'
                                                >
                                                  <Icon>close</Icon>
                                                </IconButton>
                                              </div>
                                            </Box>
                                          </Box>
                                        </Grid>
                                        {field.value && (
                                          <div style={{ padding: 5 }}>
                                            <b>
                                              <Trans>In Pacific time:</Trans>
                                            </b>{' '}
                                            {formatToTimeZone(
                                              moment
                                                .utc(field.value)
                                                .local(true),
                                              datetimeFormat,
                                              {
                                                timeZone: 'Canada/Pacific'
                                              }
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )
                                  }}
                                </Field>
                              </Grid>
                            </Grid>

                            <div style={{ padding: 5 }}>
                              <Field name={`streamList.${index}.reminderTime`}>
                                {({ field, form }) => {
                                  return (
                                    <>
                                      <Grid
                                        container
                                        direction='row'
                                        wrap='nowrap'
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            gap: 2
                                          }}
                                        >
                                          <FormTopLabel size='small'>
                                            <Trans>REMINDER_DATE_LABEL</Trans>
                                          </FormTopLabel>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'row',
                                              width: '100%'
                                            }}
                                          >
                                            <DateTimePicker
                                              format={datetimeFormat}
                                              value={field.value || null}
                                              onChange={(e) => {
                                                form.setFieldValue(
                                                  `streamList.${index}.reminderTime`,
                                                  e
                                                )
                                              }}
                                              sx={{ width: '95%' }}
                                              inputVariant='outlined'
                                              renderInput={(props) => {
                                                return (
                                                  <TextField
                                                    {...props}
                                                    inputProps={{
                                                      ...props.inputProps,
                                                      value: field.value
                                                        ? props.inputProps.value
                                                        : '',
                                                      placeholder: ''
                                                    }}
                                                    fullWidth
                                                    variant='outlined'
                                                  />
                                                )
                                              }}
                                            />
                                            <div style={{ padding: 5 }}>
                                              <IconButton
                                                onClick={(e) => {
                                                  form.setFieldValue(
                                                    `streamList.${index}.reminderTime`,
                                                    null
                                                  )
                                                }}
                                                size='large'
                                              >
                                                <Icon>close</Icon>
                                              </IconButton>
                                            </div>
                                          </Box>
                                        </Box>
                                      </Grid>
                                      {field.value && (
                                        <div style={{ padding: 5 }}>
                                          <b>
                                            <Trans>In Pacific time:</Trans>
                                          </b>{' '}
                                          {formatToTimeZone(
                                            moment.utc(field.value).local(true),
                                            datetimeFormat,
                                            {
                                              timeZone: 'Canada/Pacific'
                                            }
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )
                                }}
                              </Field>
                            </div>

                            <div>
                              <CheckboxWithFormik
                                name={`streamList.${index}.inTesting`}
                                labelPlacement='end'
                                controlLabel={<Trans>Is in testing?</Trans>}
                              />
                              <CheckboxWithFormik
                                name={`streamList.${index}.obsolete`}
                                labelPlacement='end'
                                controlLabel={<Trans>Is obsolete?</Trans>}
                              />
                            </div>

                            <div style={{ width: '100%' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 2,
                                  width: '100%'
                                }}
                              >
                                <FormTopLabel size='small'>
                                  <Trans>Logo url</Trans>
                                </FormTopLabel>
                                <FormikFastTextFieldWithDebounce
                                  name={`streamList.${index}.logo`}
                                  fullWidth
                                  inputDebounce
                                />
                                <Typography style={{ fontWeight: 'bold' }}>
                                  <Trans>Preview:</Trans>
                                </Typography>
                                <div style={{ textAlign: 'center' }}>
                                  <img
                                    src={item.logo}
                                    alt=''
                                    style={{
                                      borderRadius: 10,
                                      maxHeight: 250,
                                      maxWidth: 250
                                    }}
                                  />
                                </div>
                              </Box>
                            </div>
                          </Box>
                        </Collapse>
                      </Box>
                    </Card>
                  </ListItem>
                )
              })}
            </List>
          </div>
        )
      }}
    </Formik>
  )
}

export const FormSelectField = ({
  name,
  formsData = [],
  label,
  isTopLabel
}) => {
  const idToLabel = {}
  formsData.forEach((obj) => {
    idToLabel[obj.id] = obj.name
  })

  return (
    <FastField name={name}>
      {({ field, form }) => {
        const { setFieldValue } = form

        return (
          <CustomAutocomplete
            label={label}
            isTopLabel={isTopLabel}
            {...field}
            freeSolo={false}
            value={field.value || ''}
            fullWidth
            getOptionLabel={(opt) => idToLabel[opt] || ''}
            options={[...formsData.map((item, index) => item.id), '']}
            onChange={(e, value) => {
              setFieldValue(name, value)
            }}
          />
        )
      }}
    </FastField>
  )
}

export const MultilanguageFormSelectField = ({
  name,
  formsData,
  label,
  lang
}) => {
  const idToLabel = {}

  formsData.forEach((obj) => {
    idToLabel[obj.id] =
      obj.fullName && obj.fullName[lang] ? obj.fullName[lang] : obj.name
  })

  return (
    <FastField name={name}>
      {({ field, form }) => {
        const { setFieldValue } = form

        return (
          <div>
            <Autocomplete
              {...field}
              freeSolo={false}
              value={field.value || ''}
              fullWidth
              getOptionLabel={(opt) => idToLabel[opt] || ''}
              options={formsData.map((item) => item.id)}
              onChange={(e, value) => {
                setFieldValue(name, value)
              }}
              renderInput={(params) => (
                <TextField variant='outlined' {...params} label={label} />
              )}
            />
          </div>
        )
      }}
    </FastField>
  )
}

const DeleteFundingStreamButton = ({ handleDelete }) => {
  const [dialogOpen, openDialog] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    setDeleting(false)
  }, [dialogOpen])

  return (
    <>
      <Dialog open={dialogOpen}>
        <div style={{ width: 600 }}>
          <DialogTitle>
            <Trans>Are you sure you want to delete that section?</Trans>
          </DialogTitle>
          <DialogContent>
            <Grid
              container
              direction='row'
              justifyContent='space-evenly'
              alignItems='center'
            >
              <Button
                disabled={deleting}
                variant='contained'
                color='primary'
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleting(true)
                  enqueueSnackbar(<Trans>Deleting</Trans>, {
                    variant: 'info'
                  })
                  handleDelete().then((r) => {
                    openDialog(false)
                  })
                }}
              >
                <Grid
                  container
                  direction='row'
                  alignItems='center'
                  justifyContent='space-evenly'
                >
                  <Trans>Yes</Trans>
                  <Icon style={{ marginLeft: 5 }}>done</Icon>
                </Grid>
              </Button>
              <Button
                disabled={deleting}
                variant='outlined'
                color='primary'
                onClick={(e) => {
                  openDialog(false)
                }}
              >
                <Grid
                  container
                  direction='row'
                  alignItems='center'
                  justifyContent='space-evenly'
                >
                  <Trans>No</Trans>
                  <Icon style={{ marginLeft: 5 }}>close</Icon>
                </Grid>
              </Button>
            </Grid>
          </DialogContent>
        </div>
      </Dialog>
      <IconButton
        onClick={() => {
          openDialog(true)
        }}
        size='large'
      >
        <Icon>delete</Icon>
      </IconButton>
    </>
  )
}

export default ConfigureFundingStreams
